/**
 * SiteContextConflicts — Site_Context SC7.
 *
 * Inline conflict warnings rendered below the SiteContextInput textarea.
 * Three severity tiers mirror the SF pack's vocabulary:
 *
 *   - HIGH   ⇒ danger-toned chip; the parent gates submit on the
 *              presence of any unacknowledged HIGH conflicts.
 *   - MEDIUM ⇒ warning-toned chip; non-blocking but visible.
 *   - LOW    ⇒ muted chip; informational.
 *
 * Each chip carries two affordances:
 *   - "Acknowledge" persists in `acknowledgedConflictIds`. The LLM
 *     still sees the conflict via the prompt-block instructions; the
 *     UI just stops nagging.
 *   - "Edit text" scrolls + selects the source span in the parent
 *     textarea so the user can fix the claim without searching.
 */
import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import type { ClaimConflict, ExtractedClaim } from '../../types/siteContext';

export interface SiteContextConflictsProps {
  claims: ExtractedClaim[];
  conflicts: ClaimConflict[];
  acknowledgedConflictIds: string[];
  onAcknowledge?: (conflictId: string) => void;
  onScrollToClaim?: (sourceSpan: string) => void;
  testIdPrefix?: string;
}

const SEVERITY_STYLES: Record<ClaimConflict['severity'], {
  wrapper: string;
  icon: string;
  badge: string;
  badgeLabel: string;
  Icon: typeof AlertTriangle;
}> = {
  high: {
    wrapper: 'border-danger/40 bg-danger/5',
    icon: 'text-danger',
    badge: 'bg-danger/15 text-danger border-danger/40',
    badgeLabel: 'HIGH',
    Icon: AlertTriangle,
  },
  medium: {
    wrapper: 'border-warning/40 bg-warning/5',
    icon: 'text-warning',
    badge: 'bg-warning/15 text-warning border-warning/40',
    badgeLabel: 'MEDIUM',
    Icon: AlertTriangle,
  },
  low: {
    wrapper: 'border-border bg-muted',
    icon: 'text-text-secondary',
    badge: 'bg-muted text-text-secondary border-border',
    badgeLabel: 'LOW',
    Icon: Info,
  },
};

export const SiteContextConflicts: React.FC<SiteContextConflictsProps> = ({
  claims,
  conflicts,
  acknowledgedConflictIds,
  onAcknowledge,
  onScrollToClaim,
  testIdPrefix = 'site-context',
}) => {
  const live = conflicts.filter(c => !acknowledgedConflictIds.includes(c.id));
  if (live.length === 0) return null;

  // Sort: high → medium → low. Stable within tier.
  const order: Record<ClaimConflict['severity'], number> = { high: 0, medium: 1, low: 2 };
  const sorted = [...live].sort((a, b) => order[a.severity] - order[b.severity]);

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-conflicts`}>
      {sorted.map((conflict) => {
        const claim = claims.find(c => c.id === conflict.claimId);
        const styles = SEVERITY_STYLES[conflict.severity];
        const Icon = styles.Icon;
        return (
          <div
            key={conflict.id}
            className={`flex items-start gap-2 p-3 border ${styles.wrapper}`}
            data-testid={`${testIdPrefix}-conflict-${conflict.id}`}
            data-severity={conflict.severity}
          >
            <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${styles.icon}`} />
            <div className="flex-1 space-y-1 text-xs min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`type-body font-bold uppercase tracking-widest px-1.5 py-0.5 border ${styles.badge}`}
                >
                  {styles.badgeLabel}
                </span>
                <span className="font-semibold text-text-primary">
                  Conflict with site evidence
                </span>
              </div>
              <div className="text-text-secondary leading-relaxed">{conflict.explanation}</div>
              <div className="text-text-muted pt-1 space-y-0.5">
                {claim && (
                  <div>
                    <span className="font-mono type-meta text-text-muted">Your claim:</span>{' '}
                    <span className="italic">&quot;{claim.claim}&quot;</span>
                  </div>
                )}
                <div>
                  <span className="font-mono type-meta text-text-muted">SED says:</span>{' '}
                  <span>{conflict.sedValue}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {onAcknowledge && (
                <button
                  type="button"
                  onClick={() => onAcknowledge(conflict.id)}
                  className="type-meta text-text-secondary hover:text-primary underline-offset-2 hover:underline"
                  data-testid={`${testIdPrefix}-conflict-${conflict.id}-ack`}
                >
                  Acknowledge
                </button>
              )}
              {onScrollToClaim && claim && (
                <button
                  type="button"
                  onClick={() => onScrollToClaim(claim.sourceSpan)}
                  className="type-meta text-text-secondary hover:text-primary underline-offset-2 hover:underline"
                  data-testid={`${testIdPrefix}-conflict-${conflict.id}-edit`}
                >
                  Edit text
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SiteContextConflicts;
